import {Component, OnInit, Optional, ChangeDetectionStrategy} from '@angular/core';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {CustomPayoutService} from '../../../../../services/custom-payout/custom-payout.service';
import {OfferService} from '../../../../../services/offer/offer.service';
import {FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {Offer} from '../../../../../models/offer';
import {ActivatedRoute, Router} from '@angular/router';
import {CustomPayout} from '../../../../../models/custom-payout';
import {PartnerTabComponent} from '../../partner-tab/partner-tab.component';

import {Partner} from '../../../../../models/partner/partner';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {InputSwitchComponent} from '../../../../layout/input-switch/input-switch.component';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {GoalService} from '../../../../../services/goal/goal.service';
import {GoalTypeService} from '../../../../../services/goal-type/goal-type.service';
import {Goal} from '../../../../../models/goal';
import {GoalType} from '../../../../../models/goal-type';
import {ElementLoaderDirective} from '../../../../../directives/element-loader/element-loader.directive';
import {Currency} from '../../../../../models/currency';
import {CurrencyService} from '../../../../../services/currency/currency.service';
import {Product} from '../../../../../models/product';
import {ProductService} from '../../../../../services/product/product.service';
import {DropdownSelectComponent} from '../../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {forkJoin} from 'rxjs';

@Component({
  selector: 'app-custom-payout-form',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    InputSwitchComponent,
    ElementLoaderDirective,
    DropdownSelectComponent,
    DropdownSelectOptionComponent
],
  templateUrl: './custom-payout-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './custom-payout-form.component.scss'
})
@MetaTitle('Custom payout form')
export class CustomPayoutFormComponent implements OnInit {

  partner: Partner | null = null;
  customPayout: CustomPayout | null = null;
  offers: Offer[] = [];
  products: Product[] = [];
  productFilterId: number | null = null;
  selectedOfferId: number | null = null;
  goals: Goal[] = [];
  goalTypes: GoalType[] = [];
  goalTypesLoading = false;
  currencies: Currency[] = [];

  form = new FormGroup({
    name: new FormControl('', Validators.required),
    goalId: new FormControl<number | null>(null, Validators.required),
    goalTypeId: new FormControl(),
    currencyId: new FormControl<number | null>(null),
    payoutAmount: new FormControl(),
    payoutPercent: new FormControl(),
    startingAt: new FormControl(),
    enabled: new FormControl(false),
    subIds: new FormArray([]),
    offers: new FormArray([]),
  });

  constructor(
    public loader: LoaderService,
    private route: ActivatedRoute,
    private router: Router,
    private snackbar: SnackbarService,
    private offerService: OfferService,
    private goalService: GoalService,
    private goalTypeService: GoalTypeService,
    private customPayoutService: CustomPayoutService,
    private currencyService: CurrencyService,
    private productService: ProductService,
    @Optional() private partnerTab: PartnerTabComponent
  ) {
  }

  ngOnInit(): void {
    this.loader.startLoading();

    this.partnerTab.partnerSubject.subscribe(partner => {
      this.partner = partner;
    })

    forkJoin([
      this.currencyService.findAllAvailable(),
      this.offerService.findAll({sort: 'name', groups: 'product'}),
      this.goalService.findAll({sort: 'name'}),
      this.productService.findAll({sort: 'name'}),
    ]).subscribe(([currencies, offers, goals, products]) => {
      this.currencies = currencies;
      this.offers = offers;
      this.goals = goals;
      this.products = products;

      if (this.route.snapshot.paramMap.has('id')) {
        const id = Number(this.route.snapshot.paramMap.get('id'));
        this.customPayoutService.findOneById(id).subscribe((customPayout: CustomPayout) => {
          this.customPayout = customPayout;
          this.form.patchValue({
            name: this.customPayout.name,
            goalId: this.customPayout.goal?.id ?? null,
            goalTypeId: this.customPayout.goalType?.id ?? null,
            currencyId: this.customPayout.currency?.id,
            payoutAmount: this.customPayout.payoutAmount,
            payoutPercent: this.customPayout.payoutPercent,
            startingAt: this.customPayout.getStartingDate(),
            enabled: this.customPayout.enabled,
          });

          for (const subId of this.customPayout.subIds) {
            this.subIdsArray.push(new FormGroup({
              subId: new FormControl(subId.subId, Validators.required),
            }));
          }

          for (const offer of this.customPayout.offers) {
            this.offersArray.push(new FormGroup({
              offerId: new FormControl(offer.offer.id, Validators.required),
            }));
          }

          this.fetchGoalTypes();
        });
      } else {
        this.loader.stopLoading();
      }
    });
  }

  getOfferName(offerId: number): string {
    return this.offers.find((offer: Offer) => offer.id === Number(offerId))?.name ?? '';
  }

  get subIdsControls(): FormGroup[] {
    return this.subIdsArray.controls as FormGroup[];
  }

  get subIdsArray(): FormArray {
    return this.form?.get('subIds') as FormArray;
  }

  addSubId(subId: HTMLInputElement): void {
    if (!subId.value) {
      return;
    }

    if (this.subIdsArray.controls.find(control => {
      return control.value.subId.toUpperCase() === subId.value.toUpperCase();
    })) {
      // Ignore duplicated value
      this.snackbar.warning('This Sub ID is a duplicate.', 2500);
      return;
    }

    this.subIdsArray.push(new FormGroup({
      subId: new FormControl(subId.value, Validators.required),
    }));

    subId.value = '';
  }

  get offersControls(): FormGroup[] {
    return this.offersArray.controls as FormGroup[];
  }

  get offersArray(): FormArray {
    return this.form?.get('offers') as FormArray;
  }

  addOffer(): void {
    if (!this.selectedOfferId) {
      return;
    }

    this.offersArray.push(new FormGroup({
      offerId: new FormControl(this.selectedOfferId, Validators.required),
    }));

    this.selectedOfferId = null;
  }

  changeProductFilter(): void {
    this.selectedOfferId = null;
  }

  submit(): void {
    if (this.form.invalid) {
      return;
    }

    const customPayout = this.customPayout;
    const customPayoutData: any = this.form.value;

    if (this.partner && this.partner.id) {
      customPayoutData['partnerId'] = this.partner.id;
    }

    if (customPayout && customPayout.id) {
      this.customPayoutService.update(customPayout.id, customPayoutData).subscribe(() => {
        this.redirectSuccess(`The custom payout ${customPayout.name} has been updated.`);
      });
    } else {
      this.customPayoutService.create(customPayoutData).subscribe(() => {
        this.redirectSuccess(`The custom payout ${customPayoutData['name']} has been created.`);
      });
    }
  }

  private redirectSuccess(message: string): void {
    this.snackbar.success(message);

    if (this.partner && this.partner.id) {
      this.router.navigateByUrl(`/partners/${this.partner.id}/custom-payouts`).then();
    }
  }

  getAvailableOffers(): Offer[] {
    return this.offers.filter(offer => {
      if (this.productFilterId && offer.product?.id !== this.productFilterId) {
        return false;
      }

      return this.offersArray.controls.findIndex(control => {
        return control.value.offerId == offer.id
      }) == -1;
    });
  }

  changeGoal(): void {
    this.form.get('goalTypeId')?.setValue(null);
    this.fetchGoalTypes();
  }

  fetchGoalTypes(): void {
    this.goalTypes = [];
    if (this.form.get('goalId')?.value) {
      this.goalTypesLoading = true;
      const goalId = this.form.get('goalId')?.value;
      this.goalTypeService.findAll({goalId, sort: 'name'}).subscribe((goalTypes: GoalType[]) => {
        this.goalTypes = goalTypes;
        this.goalTypesLoading = false;
        this.loader.stopLoading();
      });
    }
  }
}
